import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { Reorder, useDragControls } from "framer-motion";
import { MdDeleteOutline, MdDragIndicator, MdEdit } from "react-icons/md";
import { deleteWatchlists, reorderWatchlists, renameWatchlist } from "../../api/user";

// One manageable list row: drag handle + (name | rename input) + rename/delete.
function ManageRow({ list, editing, draft, setDraft, onStartRename, onCommitRename, onDelete, onDragCommit }) {
  const controls = useDragControls();
  return (
    <Reorder.Item
      as="div"
      value={list}
      dragListener={false}
      dragControls={controls}
      onDragEnd={onDragCommit}
      className="flex items-center gap-2 bg-white py-3"
    >
      <span
        onPointerDown={(e) => controls.start(e)}
        className="shrink-0 cursor-grab touch-none text-tertiary"
        aria-label="Drag to reorder"
      >
        <MdDragIndicator size={20} />
      </span>
      {editing ? (
        <input
          value={draft}
          autoFocus
          maxLength={24}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={onCommitRename}
          onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
          className="min-w-0 flex-1 border-b border-dashed border-gray-300 bg-transparent text-sm font-medium text-gray-900 focus:outline-none"
        />
      ) : (
        <span className="min-w-0 flex-1 truncate text-sm font-medium text-gray-900">{list.name}</span>
      )}
      <button type="button" aria-label={`Rename ${list.name}`} onClick={onStartRename} className="shrink-0 text-secondary hover:text-primaryColor">
        <MdEdit size={16} />
      </button>
      <button type="button" aria-label={`Delete ${list.name}`} onClick={onDelete} className="shrink-0 text-red-500 hover:text-red-600">
        <MdDeleteOutline size={20} />
      </button>
    </Reorder.Item>
  );
}

// List-level management: rename, reorder (drag), and delete (confirm) personal
// lists. Curated lists are not listed — they cannot be edited.
export default function ManageListsModal({ userLists, onClose, onDeleted, onReordered, onRenamed }) {
  const [order, setOrder] = useState(userLists);
  const orderRef = useRef(userLists);
  useEffect(() => {
    setOrder(userLists);
    orderRef.current = userLists;
  }, [userLists]);

  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState("");

  const persistOrder = async () => {
    onReordered?.(orderRef.current);
    try {
      await reorderWatchlists(orderRef.current.map((l) => l.id));
    } catch (e) {
      console.error(e);
      toast.error("Could not save the watchlist order. Try again.");
    }
  };

  const commitRename = async (list) => {
    const name = draft.trim();
    setEditingId(null);
    if (!name || name === list.name) return;
    onRenamed?.(list.id, name);
    try {
      await renameWatchlist(list.id, name);
    } catch (e) {
      console.error(e);
      toast.error("Could not rename the watchlist. Try again.");
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      const res = await deleteWatchlists([pendingDelete.id]);
      if (res?.status === 200) {
        toast.success(`"${pendingDelete.name}" deleted`);
        onDeleted?.(pendingDelete.id);
      } else {
        toast.error("Could not delete the watchlist. Try again.");
      }
    } catch (e) {
      console.error(e);
      toast.error("Could not delete the watchlist. Try again.");
    } finally {
      setDeleting(false);
      setPendingDelete(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 px-4">
      <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-lg">
        {pendingDelete ? (
          <>
            <h3 className="mb-2 text-lg font-bold text-[#004d4d]">Delete list</h3>
            <p className="mb-5 text-sm text-secondary">Delete "{pendingDelete.name}"? This cannot be undone.</p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setPendingDelete(null)}
                disabled={deleting}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-secondary"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={deleting}
                className="rounded-lg bg-red-500 px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
              >
                {deleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="mb-1 flex items-center justify-between">
              <h3 className="text-lg font-bold text-[#004d4d]">Manage lists</h3>
              <button type="button" onClick={onClose} className="text-sm font-medium text-secondary hover:text-primaryColor">
                Close
              </button>
            </div>
            <p className="mb-3 text-xs text-secondary">Drag to reorder, rename, or delete your lists</p>

            {order.length === 0 ? (
              <p className="py-6 text-center text-sm text-secondary">No personal lists to manage</p>
            ) : (
              <Reorder.Group
                axis="y"
                values={order}
                onReorder={(newOrder) => {
                  setOrder(newOrder);
                  orderRef.current = newOrder;
                }}
                as="div"
                className="divide-y divide-gray-100"
              >
                {order.map((list) => (
                  <ManageRow
                    key={list.id}
                    list={list}
                    editing={editingId === list.id}
                    draft={draft}
                    setDraft={setDraft}
                    onStartRename={() => {
                      setEditingId(list.id);
                      setDraft(list.name);
                    }}
                    onCommitRename={() => commitRename(list)}
                    onDelete={() => setPendingDelete(list)}
                    onDragCommit={persistOrder}
                  />
                ))}
              </Reorder.Group>
            )}
          </>
        )}
      </div>
    </div>
  );
}
